import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Calendar, CheckCircle2, Circle, Clock, Filter, ListTodo } from "lucide-react";
import { format } from "date-fns";
import { useMFOItems } from "@/hooks/use-mfo-items";

interface SubTask {
  id: string;
  text: string;
  completed: boolean;
  created_at: string;
  updated_at: string;
}

interface MFO {
  id: string;
  code: string;
  title: string;
  priority: string;
  status: string;
  progress: number;
  dueDate: string;
  departmentName: string;
  successIndicators: string[];
  collaborators: {
    id: string;
    name: string;
    avatar: string;
  }[];
  sub_tasks: SubTask[];
}

const MFOSubTasks = () => {
  const queryClient = useQueryClient();
  const { getMFODetail } = useMFOItems();
  const [isLoading, setIsLoading] = useState(true);
  const [mfos, setMFOs] = useState<MFO[]>([]);
  const [selectedMFO, setSelectedMFO] = useState<string>("all");
  const [filteredMFOs, setFilteredMFOs] = useState<MFO[]>([]);

  // Fetch MFOs and their sub-tasks
  useEffect(() => {
    const fetchSubTasks = async () => {
      try {
        setIsLoading(true);
       

        // Fetch MFOs with their sub-tasks using more detailed query
        const { data: mfoData, error: mfoError } = await supabase
          .from('mfo_items')
          .select(`
            id,
            code,
            title,
            priority,
            status,
            progress,
            due_date,
            budget,
            department_id,
            departments (
              id,
              name
            ),
            mfo_subtasks (
              id,
              text,
              completed,
              created_at,
              updated_at
            ),
            mfo_success_indicators (
              indicator
            ),
            mfo_assignees (
              employee:employees (
                id,
                name,
                avatar
              )
            )
          `)
          .order('priority', { ascending: false });

        if (mfoError) {
          console.error("Error fetching MFO data:", mfoError);
          return;
        }

        // Transform and sort the data with more details
        const transformedData: MFO[] = mfoData.map(mfo => ({
          id: mfo.id,
          code: mfo.code,
          title: mfo.title,
          priority: mfo.priority,
          status: mfo.status,
          progress: mfo.progress,
          dueDate: mfo.due_date,
          departmentName: mfo.departments?.name,
          successIndicators: mfo.mfo_success_indicators?.map(si => si.indicator) || [],
          collaborators: mfo.mfo_assignees?.map(a => ({
            id: a.employee.id,
            name: a.employee.name,
            avatar: a.employee.avatar
          })) || [],
          sub_tasks: (mfo.mfo_subtasks || []).map(task => ({
            id: task.id,
            text: task.text,
            completed: task.completed,
            created_at: task.created_at,
            updated_at: task.updated_at
          }))
        }));

        
        setMFOs(transformedData);
        setFilteredMFOs(transformedData);
        
        // Update React Query cache with the detailed data
        queryClient.setQueryData(['mfo-subtasks'], transformedData);
        
        // Also cache individual MFO details for potential direct access
        transformedData.forEach(mfo => {
          queryClient.setQueryData(['mfoItems', mfo.id], mfo);
        });

      } catch (error) {
        console.error("Error loading sub-tasks:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubTasks();
  }, [queryClient]);

  // Handle MFO filter change
  const handleMFOFilterChange = (value: string) => {
    setSelectedMFO(value);
    if (value === "all") {
      setFilteredMFOs(mfos);
    } else {
      setFilteredMFOs(mfos.filter(mfo => mfo.id === value));
    }
  };

  // Get priority badge color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Calculate completion percentage for an MFO's sub-tasks
  const getCompletionPercentage = (subTasks: SubTask[]) => {
    if (!subTasks.length) return 0;
    const completed = subTasks.filter(task => task.completed).length;
    return Math.round((completed / subTasks.length) * 100);
  };

  // Toggle sub-task status
  const toggleSubTaskStatus = async (mfoId: string, subTaskId: string, currentStatus: boolean) => {
    try {
      const newStatus = !currentStatus;
      
      const { error } = await supabase
        .from('mfo_subtasks')
        .update({ 
          completed: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', subTaskId);

      if (error) throw error;

      // Update local state with more details
      const updatedMFOs = mfos.map(mfo => {
        if (mfo.id === mfoId) {
          const updatedSubTasks = mfo.sub_tasks.map(task => 
            task.id === subTaskId 
              ? { ...task, completed: newStatus } 
              : task
          );
          
          // Recalculate MFO progress based on sub-tasks
          const completedTasks = updatedSubTasks.filter(t => t.completed).length;
          const totalTasks = updatedSubTasks.length;
          const newProgress = Math.round((completedTasks / totalTasks) * 100);
          
          return {
            ...mfo,
            sub_tasks: updatedSubTasks,
            progress: newProgress
          };
        }
        return mfo;
      });

      setMFOs(updatedMFOs);
      setFilteredMFOs(
        selectedMFO === "all" 
          ? updatedMFOs 
          : updatedMFOs.filter(mfo => mfo.id === selectedMFO)
      );

      // Update React Query cache
      queryClient.setQueryData(['mfo-subtasks'], updatedMFOs);
      
      // Update the individual MFO cache as well
      const updatedMFO = updatedMFOs.find(mfo => mfo.id === mfoId);
      if (updatedMFO) {
        queryClient.setQueryData(['mfoItems', mfoId], updatedMFO);
      }

    } catch (error) {
      console.error("Error updating sub-task status:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-12 bg-gray-200 rounded w-1/3"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ListTodo className="h-6 w-6" />
            MFO Sub-tasks
          </h1>
          <p className="text-muted-foreground mt-1">
            View and manage sub-tasks for all MFOs
          </p>
        </div>

        <div className="flex items-center gap-4">
          <Filter className="h-5 w-5 text-muted-foreground" />
          <Select value={selectedMFO} onValueChange={handleMFOFilterChange}>
            <SelectTrigger className="w-[250px]">
              <SelectValue placeholder="Filter by MFO" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All MFOs</SelectItem>
              {mfos.map(mfo => (
                <SelectItem key={mfo.id} value={mfo.id}>
                  {mfo.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-6">
        {filteredMFOs.map(mfo => (
          <Card key={mfo.id} className="overflow-hidden">
            <CardHeader className="bg-muted/50">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold">
                  {mfo.title}
                </CardTitle>
                <Badge className={getPriorityColor(mfo.priority)}>
                  {mfo.priority}
                </Badge>
              </div>
              <div className="mt-2">
                <Progress value={getCompletionPercentage(mfo.sub_tasks)} className="h-2" />
                <p className="text-sm text-muted-foreground mt-1">
                  {mfo.sub_tasks.filter(t => t.completed).length} of {mfo.sub_tasks.length} tasks completed
                </p>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {mfo.sub_tasks.length > 0 ? (
                <div className="space-y-4">
                  {mfo.sub_tasks.map(task => (
                    <div
                      key={task.id}
                      className="flex items-start gap-4 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                    >
                      <Checkbox
                        checked={task.completed}
                        onCheckedChange={() => toggleSubTaskStatus(mfo.id, task.id, task.completed)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className={`font-medium ${task.completed ? "line-through text-muted-foreground" : ""}`}>
                            {task.text}
                          </h3>
                          {task.completed && (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-2">
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Created: {task.created_at ? format(new Date(task.created_at), 'MMM d, yyyy') : 'N/A'}
                          </span>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Updated: {task.updated_at ? format(new Date(task.updated_at), 'MMM d, yyyy') : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Circle className="h-8 w-8 mx-auto mb-2" />
                  <p>No sub-tasks found for this MFO</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {filteredMFOs.length === 0 && (
          <div className="text-center py-12">
            <ListTodo className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No MFOs found</h3>
            <p className="text-muted-foreground">
              {selectedMFO === "all" 
                ? "There are no MFOs with sub-tasks in the system."
                : "No MFOs match your filter criteria."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MFOSubTasks;
